import { Tetromino } from "@/components/Tetromino";
import type { PieceType } from "@/game/pieces";
import { avatarUrl } from "@/lib/playersApi";

/**
 * The framed picture that stands for a player.
 *
 * Written once because the ladder — picture, else favourite piece, else a plain
 * stand-in — had four hand-spelled copies that had each been repaired
 * differently: only one cache-busted, only one coped with a missing file, and
 * they disagreed on the fallback. A member who removed their photograph saw a
 * stale image in one place and a piece in another.
 *
 * `hasAvatar` comes from the server rather than being discovered by letting an
 * <img> 404, so a player who wrote a bio but never uploaded anything costs no
 * request at all.
 */
export function PlayerAvatar({
  username,
  hasAvatar,
  piece = null,
  fallback = null,
  size,
  cell,
  version,
}: {
  readonly username: string;
  readonly hasAvatar: boolean;
  readonly piece?: PieceType | null;
  /** Shown when there is neither a picture nor a piece — an initial, usually. */
  readonly fallback?: React.ReactNode;
  /** Tailwind size classes for the frame, e.g. "h-12 w-12". */
  readonly size: string;
  readonly cell: number;
  /** Bumped after an upload so the replaced picture is not read from cache. */
  readonly version?: number;
}) {
  return (
    <span
      className={`flex ${size} shrink-0 items-center justify-center overflow-hidden border-2 border-ink bg-paper`}
    >
      {hasAvatar ? (
        <img
          src={
            version
              ? `${avatarUrl(username)}?v=${version}`
              : avatarUrl(username)
          }
          alt=""
          className="h-full w-full object-cover"
        />
      ) : piece ? (
        <Tetromino type={piece} cell={cell} />
      ) : (
        (fallback ?? <span className="meta text-[0.625rem] opacity-40">—</span>)
      )}
    </span>
  );
}
